import { ResponsiveSelect, SelectItem } from "@components/responsive-select";
import { UpgradeToHighQualityButton } from "@components/upgrade-to-high-quality-button";
import { LorasType } from "@features/loras/type";
import { getLoraPreview, useLora, useLoras } from "@features/loras/use-loras";
import { usePopularLoras } from "@features/popular/use-popular";
import { useUser } from "@features/user/use-user";
import { useNavigateAlt } from "@features/utils";
import { MagnifyingGlassIcon, PencilIcon } from "@heroicons/react/24/solid";
import { Flex, IconButton, Tooltip } from "@radix-ui/themes";
import { useLoraSearchModal } from "@views/app/lora/components/search-lora-modal";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export const LoraSelect = ({
  value: model,
  onChange: setModel,
  canEdit,
  type,
}: {
  type?: "model" | "style";
  value: string | "none";
  onChange: (value: string | "none", model: LorasType | undefined) => void;
  canEdit?: boolean;
}) => {
  const { site } = useParams();
  const selectedLora = useLora(model === "none" ? "" : model);
  const { loras } = useLoras();
  const navigateAlt = useNavigateAlt();
  const { user } = useUser();
  const { t } = useTranslation();
  const { setOpen: openSearchModal } = useLoraSearchModal();

  // Get popular loras for the public section
  const popularLorasQuery = usePopularLoras(
    type === "style" ? "lora_style" : "lora_model",
    ""
  );
  const popularLoras = _.uniqBy(
    [
      selectedLora.data,
      ...(popularLorasQuery.data?.pages.flatMap((page) => page) || []),
    ].filter(Boolean),
    "id"
  ) as LorasType[];

  // Get the current lora if it exists
  const currentLora =
    selectedLora.data || loras.data?.find((a) => a.id === model);

  // Filter user's models based on type and status
  let userModels = (loras.data || []).filter(
    (a) => (!type || a.type === type) && a.status !== "failed"
  );

  // Filter popular models if search is active
  let filteredPopularLoras = popularLoras;

  // Make sure the current lora is always included in the list (if it exists)
  if (
    currentLora &&
    model !== "none" &&
    !userModels.some((m) => m.id === currentLora.id) &&
    !filteredPopularLoras.some((m) => m.id === currentLora.id)
  ) {
    userModels = [currentLora, ...userModels];
  }

  // Prepare items for the ResponsiveSelect
  const selectItems: SelectItem[] = [
    { value: "none", label: t("general.none", "None") },
  ];

  // Add search in library option
  selectItems.push({
    value: "search",
    label: (
      <Flex as="span" align="center" gap="2">
        <MagnifyingGlassIcon className="w-4 h-4" />
        {t("search.search_library", "Search in library...")}
      </Flex>
    ),
  });

  // Add user's personal models
  _.sortBy(
    _.uniq(userModels.filter((m) => !m.public).map((a) => a.subtype)),
    (a) => t(["loras.subtypes." + a, "loras.types." + a, a])
  ).forEach((subtype) => {
    const subtypeModels = _.sortBy(
      userModels.filter((a) => a.subtype === subtype && !a.public),
      (a) => a.name
    );

    if (subtypeModels.length > 0) {
      subtypeModels.forEach((model) => {
        selectItems.push({
          value: model.id,
          label: (
            <Flex as="span" align="center" gap="2">
              <img
                src={getLoraPreview(model.id, model.preview_status)}
                className="w-6 h-6 rounded-full"
                alt={model.name}
              />
              {model.name}{" "}
              {model.technology === "images" && model.subtype === "avatar" && (
                <Tooltip content="Standard, low-likeness model">
                  <i>(LQ) </i>
                </Tooltip>
              )}
              {model.id &&
                model.status !== "completed" &&
                ` (${t("general.loading", "Loading")})`}
            </Flex>
          ),
          disabled: model.status !== "completed",
          group: t([
            "loras.subtypes." + subtype,
            "loras.types." + subtype,
            subtype,
          ]),
        });
      });
    }
  });

  // Add public and popular models
  filteredPopularLoras
    .filter(
      (popularLora) =>
        !userModels.some((userModel) => userModel.id === popularLora.id)
    )
    .forEach((lora) => {
      selectItems.push({
        value: lora.id,
        label: (
          <Flex as="span" align="center" gap="2">
            <img
              src={getLoraPreview(lora.id, lora.preview_status)}
              className="w-6 h-6 rounded-full"
              alt={lora.name}
            />
            {lora.name}{" "}
            {lora.technology === "images" && lora.subtype === "avatar" && (
              <Tooltip content="Standard, low-likeness model">
                <i>(LQ) </i>
              </Tooltip>
            )}
            {lora.status !== "completed" &&
              ` (${t("general.loading", "Loading")})`}
          </Flex>
        ),
        disabled: lora.status !== "completed",
        group: t("loras.subtypes.public", "Public"),
      });
    });

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2">
        <ResponsiveSelect
          value={model}
          onChange={(e) => {
            if (e === "search") {
              // Open the search modal
              openSearchModal(
                type === "model" ? "models" : "styles",
                (id, model) => {
                  setModel(id, model);
                }
              );
              return;
            }

            setModel(
              e,
              userModels.find((a) => a.id === e) ||
                filteredPopularLoras.find((a) => a.id === e)
            );
          }}
          items={selectItems}
          size="3"
          className="grow"
          placeholder={t("general.select_model", "Select a model")}
          searchPlaceholder={t("search.search_models", "Search models...")}
          renderTrigger={() => (
            <Flex as="span" align="center" gap="2">
              {currentLora && (
                <img
                  src={getLoraPreview(model, currentLora.preview_status)}
                  className="w-6 h-6 rounded-full"
                  alt={"Aperçu du modèle"}
                />
              )}
              {currentLora?.name || t("general.none", "None")}{" "}
              {currentLora?.technology === "images" &&
                currentLora?.subtype === "avatar" && (
                  <Tooltip content="Standard, low-likeness model">
                    <i>(LQ) </i>
                  </Tooltip>
                )}
              {currentLora?.id &&
                currentLora?.status !== "completed" &&
                ` (${t("general.loading", "Loading")})`}
            </Flex>
          )}
        />

        {/* Edit button */}
        {canEdit &&
          currentLora &&
          currentLora?.owner_id === user?.data?.id &&
          model !== "none" && (
            <IconButton
              variant="outline"
              color="gray"
              size="3"
              className="shrink-0"
              onClick={(event) =>
                navigateAlt(`/${site}/lora/${currentLora?.id}`, event)
              }
            >
              <Tooltip content={t("general.edit", "Edit model")}>
                <PencilIcon className="w-4 h-4" />
              </Tooltip>
            </IconButton>
          )}
      </div>

      {canEdit &&
        currentLora?.subtype === "avatar" &&
        currentLora?.technology === "images" && (
          <UpgradeToHighQualityButton size="3" />
        )}
    </div>
  );
};
